/*
 * Pfeffinger: reservation (Klaviyo Back in Stock) for products tagged "in-fertigung".
 * Uses only Klaviyo's public client API (company_id = public site ID). No payment, no order.
 * Markup: snippets/pf-reservieren.liquid
 */
(() => {
  if (window.customElements.get('pf-reservation')) {
    return;
  }

  const KLAVIYO_REVISION = '2026-07-15';
  const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  class PfReservation extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-pf-fields] button');
      this.emailInput = this.querySelector('[data-pf-email]');
      this.newsletterInput = this.querySelector('[data-pf-newsletter]');
      this.statusElement = this.querySelector('[data-pf-status]');

      this.onClick = this.submit.bind(this);
      this.onKeydown = (event) => {
        if (event.key === 'Enter') {
          // Prevent the surrounding product form from being submitted
          event.preventDefault();
          this.submit();
        }
      };

      this.button?.addEventListener('click', this.onClick);
      this.emailInput?.addEventListener('keydown', this.onKeydown);
    }

    disconnectedCallback() {
      this.button?.removeEventListener('click', this.onClick);
      this.emailInput?.removeEventListener('keydown', this.onKeydown);
    }

    get message() {
      return {
        success: this.querySelector('template[data-pf-success]')?.innerHTML.trim(),
        error: this.querySelector('template[data-pf-error]')?.innerHTML.trim()
      };
    }

    showStatus(type, text) {
      this.statusElement.innerHTML = '';
      const banner = document.createElement('div');
      banner.className = `banner banner--${type}`;
      banner.innerHTML = text;
      this.statusElement.appendChild(banner);
    }

    async submit() {
      if (this.button.getAttribute('aria-busy') === 'true') {
        return;
      }

      const email = (this.emailInput.value || '').trim();

      if (!EMAIL_PATTERN.test(email)) {
        this.emailInput.setAttribute('aria-invalid', 'true');
        this.showStatus('error', this.message.error);
        this.emailInput.focus();
        return;
      }

      this.emailInput.removeAttribute('aria-invalid');
      this.button.setAttribute('aria-busy', 'true');

      try {
        await this.subscribeBackInStock(email);

        if (this.newsletterInput?.checked) {
          // Newsletter is optional: a failure here must not fail the reservation itself
          try {
            await this.subscribeNewsletter(email);
          } catch (error) {
            console.warn('[pf-reservation] newsletter subscription failed', error);
          }
        }

        this.querySelector('[data-pf-fields]').hidden = true;
        this.showStatus('success', this.message.success);
      } catch (error) {
        console.warn('[pf-reservation] reservation failed', error);
        this.showStatus('error', this.message.error);
      } finally {
        this.button.removeAttribute('aria-busy');
      }
    }

    async post(endpoint, payload) {
      if (this.hasAttribute('test-mode')) {
        // Test mode: nothing is sent to Klaviyo
        console.info('[pf-reservation] TEST MODE, not sent:', endpoint, payload);
        await new Promise((resolve) => setTimeout(resolve, 400));

        if (payload.data.attributes.profile.data.attributes.email.endsWith('@fehler.test')) {
          throw new Error('Simulated error (test mode)');
        }

        return;
      }

      const response = await fetch(`https://a.klaviyo.com/client/${endpoint}/?company_id=${encodeURIComponent(this.getAttribute('company-id'))}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/vnd.api+json',
          'Accept': 'application/vnd.api+json',
          'revision': KLAVIYO_REVISION
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error(`Klaviyo responded with status ${response.status}`);
      }
    }

    subscribeBackInStock(email) {
      return this.post('back-in-stock-subscriptions', {
        data: {
          type: 'back-in-stock-subscription',
          attributes: {
            channels: ['EMAIL'],
            profile: {
              data: {
                type: 'profile',
                attributes: { email }
              }
            }
          },
          relationships: {
            variant: {
              data: {
                type: 'catalog-variant',
                id: `$shopify:::$default:::${this.getAttribute('variant-id')}`
              }
            }
          }
        }
      });
    }

    subscribeNewsletter(email) {
      return this.post('subscriptions', {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: this.getAttribute('source') || 'Reservierung',
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email,
                  subscriptions: {
                    email: {
                      marketing: { consent: 'SUBSCRIBED' }
                    }
                  }
                }
              }
            }
          },
          relationships: {
            list: {
              data: {
                type: 'list',
                id: this.getAttribute('list-id')
              }
            }
          }
        }
      });
    }
  }

  window.customElements.define('pf-reservation', PfReservation);
})();
